import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  OnInit,
  signal,
  SimpleChanges,
  OnChanges,
} from '@angular/core';
import { FieldArrayType, FormlyModule } from '@ngx-formly/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatDividerModule } from '@angular/material/divider';
import { FormArray } from '@angular/forms';
import { InfoTooltipComponent } from '../info-tooltip.component';
import { UserStore } from '../../../core/services/user.store';
import { takeUntil } from 'rxjs/operators';
import { injectDestroy } from 'ngxtension/inject-destroy';
import { FormService } from '../../../features/admin/modules/features/form-builder/form.service';

@Component({
  selector: 'formly-repeat-section',
  template: `
    <div class="my-4" [attr.data-cy]="field.props?.keyPath ?? null">
      @if (props.label && !props.hideLabel) {
        @if (props.labelType === undefined || props.labelType === 'titel') {
          <h3 class="font-bold mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </h3>
        } @else {
          <label class="block text-sm font-medium text-default mb-2">
            {{ props.label }}
            <app-info-tooltip [tooltip]="props.info"></app-info-tooltip>
          </label>
        }
      }

      @if (props.description) {
        <p class="mb-2">{{ props.description }}</p>
      }
      <div class="flex w-full flex-wrap">
        <!-- track by config object identity, NOT $index: with $index, removing row i destroys
             the LAST view while its (surviving) config stays in fieldGroup — Angular then runs
             ngOnDestroy against a live config, firing its hooks.onDestroy and silently tearing
             down state (e.g. the conditions editor's stored-value restore) on the wrong row.
             Object identity rather than field.id: formly never regenerates an id it finds on a
             config, so a stored generatedForm that carries a serialized id would give every
             cloned row the same id (duplicate track keys, NG0955). -->
        @for (fieldGroupField of field.fieldGroup; track fieldGroupField) {
          <div class="repeater-item flex gap-4 w-full">
            @if (props.repeatLabel) {
              <span class="text-default font-medium flex-0"> #{{ $index + 1 }} {{ props.repeatLabel }} </span>
            }
            <formly-field class="flex-1 max-w-full" [field]="fieldGroupField"></formly-field>
            @if (
              !form.disabled &&
              !props.hideButtons &&
              !field.props.disabled &&
              props.initialCount < field.fieldGroup.length &&
              !hideAddButton()
            ) {
              <button type="button" mat-button class="flex-0 max-h-fit " (click)="remove($index)">
                <mat-icon>close</mat-icon>
              </button>
            }
          </div>
          @if (props.seperatorText && $index + 1 !== model.length) {
            <h4 class="mt-2 mb-4 text-center text-default" [innerHTML]="props.seperatorText"></h4>
          }
        } @empty {
          <p class="text-sm text-accent">Geen {{ props.label ? props.label.toLowerCase() : 'items' }} aangemaakt</p>
        }
      </div>
      @if (!props.hideButtons) {
        <div class="pt-2 pb-4">
          @if (!form.disabled && !field.props.disabled && !hideAddButton()) {
            <button
              type="button"
              mat-flat-button
              class="small-button dd-action-button"
              [attr.aria-label]="props.addText || 'Toevoegen'"
              (click)="add(field.fieldGroup?.length, getDefaultModelForIndex(field.fieldGroup?.length))">
              <mat-icon>add</mat-icon> {{ props.addText }}
            </button>
          }
        </div>
      }
    </div>
  `,
  imports: [
    FormlyModule,
    MatButtonModule,
    MatDividerModule,
    MatIconModule,
    MatExpansionModule,
    MatInputModule,
    InfoTooltipComponent,
  ],
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyRepeatTypeComponent extends FieldArrayType implements OnInit, OnChanges {
  cd = inject(ChangeDetectorRef);
  userStore = inject(UserStore);
  formService = inject(FormService);
  hideAddButton = signal(false);
  private destroy$ = injectDestroy();
  private initialized = false;

  constructor() {
    super();
  }

  ngOnInit() {
    if (!(this.field.formControl instanceof FormArray)) {
      this.field.formControl = new FormArray([]);
    }

    this.field.formControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.enforceMinimumLength();
    });
    this.initialize();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['field'] && this.field?.props) {
      this.initialize();
    }
  }

  remove(i: number) {
    if (!this.model) return;
    // Formly's FieldArrayType.remove also re-keys the surviving configs and unregisters the
    // removed control. The previous hand-rolled removeAt/splice skipped that re-keying, so a
    // surviving row's key kept pointing at its removed neighbour's model slot — field.model
    // then resolved to the wrong row for every later read and write.
    //
    // Deliberate: super.remove marks the form dirty (the old code did not). A removal IS a
    // user edit — leaving the form pristine made the dirty-keyed cross-page save sweep drop
    // removals on non-active pages. Pass { markAsDirty: false } here if that ever needs undoing.
    super.remove(i);
    this.cd.markForCheck();
  }

  private initialize() {
    if (this.props.initialCount === undefined) {
      this.props.initialCount = 0;
    }

    // Apply defaultCount only once at initialization
    if (!this.initialized) {
      this.applyDefaultCount();
      this.initialized = true;
    }

    if (this.props.hideAddButtonForRoles != null && Object.keys(this.props.hideAddButtonForRoles).length > 0) {
      this.hideAddButton.set(this.field.props.hideAddButtonForRoles[this.userStore.currentUserId()]);
    }
  }

  /**
   * Get the default model for a specific index, delegating to FormService.
   */
  getDefaultModelForIndex(index: number): any {
    return this.formService.getDefaultModelForIndex(this.field, index, this.props.defaultModel);
  }

  /**
   * Apply defaultCount only for NEW entries (when there's no existing data).
   * If there's already saved data, we just load that - defaultCount is not enforced.
   * initialCount (minimum) is handled separately by enforceMinimumLength().
   */
  private applyDefaultCount() {
    // Only apply defaultCount for new entries — not for edits.
    // this.model?.length check alone is unreliable: at ngOnInit the model may
    // still be an empty array while entry data is loading, causing defaults to
    // be injected into existing entries.
    if (!this.options?.formState?.isNew || this.model?.length > 0) {
      return;
    }

    const targetCount = this.props.defaultCount ?? this.props.initialCount;
    // Capture starting length BEFORE the loop - fieldGroup.length changes after each add()
    const startingLength = this.field.fieldGroup?.length ?? 0;

    if (startingLength < targetCount) {
      const itemsToAdd = targetCount - startingLength;
      for (let i = 0; i < itemsToAdd; i++) {
        const currentIndex = startingLength + i;
        this.add(currentIndex, this.getDefaultModelForIndex(currentIndex));
      }
    }
  }

  /**
   * Enforce minimum length (initialCount) on value changes
   * This does NOT use defaultCount - that's only for initial load
   */
  private enforceMinimumLength() {
    const startingLength = this.field.fieldGroup?.length ?? 0;
    if (startingLength < this.props.initialCount) {
      const itemsToAdd = this.props.initialCount - startingLength;
      for (let i = 0; i < itemsToAdd; i++) {
        const currentIndex = startingLength + i;
        this.add(currentIndex, this.getDefaultModelForIndex(currentIndex));
      }
    }
  }
}
